import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AdminPage } from "@/pages/admin/AdminLayout";
import { SearchPerformance } from "@/components/admin/SearchPerformance";
import { SeoHealth } from "@/components/admin/SeoHealth";
import {
  AlertTriangle, CheckCircle2, CircleDashed, ExternalLink, Info, UserCog,
} from "lucide-react";

const TABS = [
  ["search", "Search performance"],
  ["health", "Indexing & SEO health"],
  ["setup", "Setup & tracking"],
  ["keywords", "Keyword opportunities"],
  ["categories", "Category priorities"],
  ["social", "Social plan"],
  ["calendar", "30-day calendar"],
  ["guides", "Proposed guides"],
];

const PRIORITY_STYLE = {
  P0: "bg-sage text-white",
  P1: "bg-sage/20 text-sage-dark",
  P2: "bg-[#EDEAE3] text-[#6E6A63]",
};

const EVIDENCE_LABEL = {
  measured: ["Measured", "bg-sage text-white"],
  third_party: ["Third-party research", "bg-[#E7EFE8] text-sage-dark"],
  platform_signal: ["Platform signal", "bg-[#EFEADF] text-[#7A6B4F]"],
  hypothesis: ["Our hypothesis", "bg-[#EDEAE3] text-[#6E6A63]"],
  first_party: ["Our own catalogue", "bg-[#E7EFE8] text-sage-dark"],
};

const STATUS_ICON = {
  live: CheckCircle2,
  ready: CircleDashed,
  needs_owner_action: AlertTriangle,
  partner_only: Info,
  manual: Info,
  policy: Info,
};

const Tag = ({ className = "", children }) => (
  <span
    className={`inline-block max-w-full break-words px-2 py-0.5 text-[10px] tracking-[0.1em] uppercase ${className}`}
  >
    {children}
  </span>
);

const Card = ({ title, meta, children, testId }) => (
  <div className="bg-white border border-[#E6E3DC] p-5" data-testid={testId}>
    <div className="flex items-start justify-between gap-3">
      <h3 className="display text-[17px] leading-snug text-charcoal">{title}</h3>
      {meta}
    </div>
    {children}
  </div>
);

const SetupTab = ({ data }) => (
  <div className="space-y-8" data-testid="growth-setup">
    <div className="space-y-3">
      {data.measurement_status.map((row) => {
        const Icon = STATUS_ICON[row.status] || Info;
        return (
          <div key={row.signal} className="bg-white border border-[#E6E3DC] p-4 flex gap-3">
            <Icon
              className={`w-4 h-4 mt-1 shrink-0 ${
                row.status === "live" ? "text-sage"
                  : row.status === "needs_owner_action" ? "text-[#B4762F]" : "text-[#9A968E]"
              }`}
            />
            <div>
              <div className="text-[14px] text-charcoal">{row.signal}</div>
              <p className="text-[13px] text-[#6E6A63] mt-1 leading-relaxed">{row.detail}</p>
            </div>
          </div>
        );
      })}
    </div>

    <div>
      <h2 className="text-[11px] tracking-[0.18em] uppercase text-[#9A968E] mb-3">
        Needs you (account access)
      </h2>
      <div className="grid gap-4 md:grid-cols-2">
        {data.owner_actions.map((a) => (
          <Card
            key={a.title}
            title={a.title}
            meta={<UserCog className="w-4 h-4 text-sage shrink-0" />}
            testId={`growth-action-${a.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          >
            <p className="text-[13px] text-[#6E6A63] mt-2 leading-relaxed">{a.why}</p>
            <ol className="mt-3 space-y-1.5">
              {a.steps.map((s, i) => (
                <li key={i} className="text-[13px] text-[#454545] flex gap-2">
                  <span className="text-sage">{i + 1}.</span>
                  <span>{s}</span>
                </li>
              ))}
            </ol>
          </Card>
        ))}
      </div>
    </div>

    <div>
      <h2 className="text-[11px] tracking-[0.18em] uppercase text-[#9A968E] mb-3">
        Tracking recommendations
      </h2>
      <div className="space-y-3">
        {data.tracking_recommendations.map((t) => (
          <div key={t.item} className="bg-white border border-[#E6E3DC] p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="text-[14px] text-charcoal">{t.item}</div>
              <Tag className="bg-[#EDEAE3] text-[#6E6A63] shrink-0 whitespace-normal sm:whitespace-nowrap max-w-[45%]">
                {t.status}
              </Tag>
            </div>
            <p className="text-[13px] text-[#6E6A63] mt-1.5 leading-relaxed">{t.detail}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const KeywordsTab = ({ rows }) => (
  <div className="space-y-3" data-testid="growth-keywords">
    {rows.map((k) => {
      const [label, cls] = EVIDENCE_LABEL[k.evidence_type] || EVIDENCE_LABEL.hypothesis;
      return (
        <div key={k.topic} className="bg-white border border-[#E6E3DC] p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-[10px] tracking-[0.18em] uppercase text-[#9A968E]">
                {k.category}
              </div>
              <h3 className="display text-[17px] leading-snug text-charcoal mt-1">{k.topic}</h3>
            </div>
            <Tag className={PRIORITY_STYLE[k.priority]}>{k.priority}</Tag>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Tag className={cls}>{label}</Tag>
            <span className="text-[12px] text-[#6E6A63]">Intent: {k.intent}</span>
          </div>
          <p className="text-[13px] text-[#6E6A63] mt-2.5 leading-relaxed">{k.evidence}</p>
          <dl className="mt-3 grid gap-2 sm:grid-cols-2 text-[13px]">
            <div>
              <dt className="text-[10px] tracking-[0.16em] uppercase text-[#9A968E]">Partner</dt>
              <dd className="text-[#454545]">{k.partner}</dd>
            </div>
            <div>
              <dt className="text-[10px] tracking-[0.16em] uppercase text-[#9A968E]">
                Destination page
              </dt>
              <dd>
                <a
                  href={k.page}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sage hover:text-sage-dark inline-flex items-center gap-1"
                >
                  {k.page}
                  <ExternalLink className="w-3 h-3" />
                </a>
              </dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-[10px] tracking-[0.16em] uppercase text-[#9A968E]">
                Social opportunity
              </dt>
              <dd className="text-[#454545]">{k.social}</dd>
            </div>
          </dl>
          {k.note && (
            <p className="text-[12.5px] text-[#B4762F] mt-3 flex gap-2">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              {k.note}
            </p>
          )}
        </div>
      );
    })}
  </div>
);

const CategoriesTab = ({ rows }) => (
  <div className="grid gap-4 md:grid-cols-2" data-testid="growth-categories">
    {rows.map((c) => (
      <Card
        key={c.category}
        title={c.category}
        meta={<Tag className={PRIORITY_STYLE[c.priority]}>{c.priority}</Tag>}
      >
        <p className="text-[13px] text-[#6E6A63] mt-2 leading-relaxed">{c.rationale}</p>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {c.partners.map((p) => (
            <Tag key={p} className="bg-[#EDEAE3] text-[#6E6A63] normal-case tracking-normal">
              {p}
            </Tag>
          ))}
        </div>
      </Card>
    ))}
  </div>
);

const Platform = ({ label, title, body }) => (
  <div className="border-t border-[#EDEAE3] pt-3 mt-3">
    <div className="text-[10px] tracking-[0.18em] uppercase text-sage">{label}</div>
    <div className="text-[14px] text-charcoal mt-1">{title}</div>
    <p className="text-[13px] text-[#6E6A63] mt-1 leading-relaxed">{body}</p>
  </div>
);

const SocialTab = ({ rows }) => (
  <div className="space-y-4" data-testid="growth-social">
    {rows.map((s) => (
      <Card key={s.topic} title={s.topic}>
        <p className="text-[13px] text-[#6E6A63] mt-2 leading-relaxed">
          <span className="text-[#454545]">Audience:</span> {s.audience} ·{" "}
          <span className="text-[#454545]">Intent:</span> {s.intent}
        </p>
        <Platform label="Pinterest pin" title={s.pinterest.title} body={s.pinterest.concept} />
        <Platform label="Instagram Reel" title={s.instagram.title} body={s.instagram.hook} />
        <Platform label="YouTube Short" title={s.youtube.title} body={s.youtube.concept} />
        <div className="border-t border-[#EDEAE3] pt-3 mt-3 flex flex-wrap items-center gap-2">
          <a
            href={s.page}
            target="_blank"
            rel="noreferrer"
            className="text-[13px] text-sage hover:text-sage-dark inline-flex items-center gap-1"
          >
            {s.page}
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>
        <p className="text-[12.5px] text-[#6E6A63] mt-2 italic">{s.why}</p>
      </Card>
    ))}
  </div>
);

const CalendarTab = ({ rows }) => (
  <div className="space-y-6" data-testid="growth-calendar">
    {[1, 2, 3, 4].map((week) => (
      <div key={week}>
        <h2 className="text-[11px] tracking-[0.18em] uppercase text-[#9A968E] mb-3">
          Week {week}
        </h2>
        <div className="space-y-3">
          {rows.filter((r) => r.week === week).map((r, i) => (
            <div key={i} className="bg-white border border-[#E6E3DC] p-4">
              <div className="flex flex-wrap items-center gap-2">
                <Tag className="bg-charcoal text-white">{r.day}</Tag>
                <Tag className="bg-sage/15 text-sage-dark">{r.platform}</Tag>
                <span className="text-[12px] text-[#9A968E]">{r.category}</span>
              </div>
              <div className="text-[14.5px] text-charcoal mt-2.5">{r.hook}</div>
              <div className="text-[13px] text-[#6E6A63] mt-1.5">
                Keyword: <span className="text-[#454545]">{r.keyword}</span>
              </div>
              <div className="text-[13px] mt-1">
                {r.destination.startsWith("/") ? (
                  <a
                    href={r.destination}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sage hover:text-sage-dark"
                  >
                    {r.destination}
                  </a>
                ) : (
                  <span className="text-[#6E6A63]">{r.destination}</span>
                )}
              </div>
              <div className="text-[12.5px] text-[#6E6A63] mt-2">
                {r.objective} · Watch: {r.metric}
              </div>
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

const GuidesTab = ({ rows }) => (
  <div className="space-y-4" data-testid="growth-guides">
    <p className="text-[13px] text-[#6E6A63]">
      Nothing below is written or published. Approve the ones you want and they get built.
    </p>
    {rows.map((g) => (
      <Card key={g.title} title={g.title} meta={<Tag className="bg-[#EDEAE3] text-[#6E6A63]">{g.hub}</Tag>}>
        <p className="text-[13px] text-[#6E6A63] mt-2 leading-relaxed">{g.why}</p>
        <div className="mt-3 text-[13px]">
          <span className="text-[10px] tracking-[0.16em] uppercase text-[#9A968E] block">
            Would link to
          </span>
          {g.internal_links.map((l) => (
            <a
              key={l}
              href={l}
              target="_blank"
              rel="noreferrer"
              className="text-sage hover:text-sage-dark block"
            >
              {l}
            </a>
          ))}
        </div>
      </Card>
    ))}
  </div>
);

export default function GrowthResearch() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("search");

  useEffect(() => {
    api.get("/admin/growth-research").then((r) => setData(r.data)).catch(() => setData(false));
  }, []);

  if (data === null)
    return <div className="text-[13px] text-[#6E6A63]" data-testid="growth-loading">Loading research…</div>;
  if (data === false)
    return <div className="text-[13px] text-[#B4762F]" data-testid="growth-error">Could not load the research.</div>;

  return (
    <AdminPage
      title="Organic Growth"
      lede={`${data.disclaimer} Researched ${data.researched_on}.`}
    >
      <div data-testid="admin-growth">
        <div className="flex gap-1.5 overflow-x-auto pb-1 mb-6">
          {TABS.map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              data-testid={`growth-tab-${key}`}
              className={`whitespace-nowrap px-4 py-2 text-[12.5px] transition-colors ${
                tab === key
                  ? "bg-charcoal text-white"
                  : "bg-white border border-[#E6E3DC] text-[#6E6A63] hover:text-charcoal"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "search" && (
          <SearchPerformance
            setupSteps={data.owner_actions.find((a) => a.title.includes("Connect Search Console"))
              ?.steps}
          />
        )}
        {tab === "health" && <SeoHealth />}
        {tab === "setup" && <SetupTab data={data} />}
        {tab === "keywords" && <KeywordsTab rows={data.keywords} />}
        {tab === "categories" && <CategoriesTab rows={data.category_priorities} />}
        {tab === "social" && <SocialTab rows={data.social_plan} />}
        {tab === "calendar" && <CalendarTab rows={data.calendar} />}
        {tab === "guides" && <GuidesTab rows={data.guide_shortlist} />}

        <div className="mt-10 pt-6 border-t border-[#E6E3DC]">
          <h2 className="text-[11px] tracking-[0.18em] uppercase text-[#9A968E] mb-3">
            Where the evidence came from
          </h2>
          <ul className="space-y-2">
            {data.sources.map((s) => (
              <li key={s.label} className="text-[12.5px] text-[#6E6A63]">
                <span className="text-[#454545]">{s.label}</span> — {s.used_for}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </AdminPage>
  );
}
